import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  flexRender,
} from "@tanstack/react-table";
import Navbar from "../../components/Navbar";
import { BASE_URL } from "../../const";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Checkbox } from "../../components/ui/checkbox";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../../components/ui/table";
import {
  DropdownMenu, DropdownMenuCheckboxItem, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import {
  ArrowUpDown, ArrowUp, ArrowDown, Plus, Upload, Download,
  MoreHorizontal, Pencil, Copy, Trash2, Loader2, X, RefreshCw,
  Settings2, AlertCircle, CheckCircle2,
} from "lucide-react";

function getAuthHeader() {
  const token = localStorage.getItem("token");
  if (!token) return {};
  if (token.startsWith("Bearer ") || token.startsWith("Token ")) return { Authorization: token };
  return { Authorization: `Token ${token}` };
}

function parseCSV(text) {
  const rows = [];
  let current = "", line = [], inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i], next = text[i + 1];
    if (ch === '"' && inQuotes && next === '"') { current += '"'; i++; continue; }
    if (ch === '"') { inQuotes = !inQuotes; continue; }
    if (ch === "," && !inQuotes) { line.push(current); current = ""; continue; }
    if ((ch === "\n" || ch === "\r") && !inQuotes) {
      if (ch === "\r" && next === "\n") i++;
      line.push(current); rows.push(line); line = []; current = ""; continue;
    }
    current += ch;
  }
  if (current.length > 0 || line.length > 0) { line.push(current); rows.push(line); }
  return rows;
}

function csvEscape(v) {
  const t = String(v ?? "");
  return (t.includes(",") || t.includes('"') || t.includes("\n")) ? `"${t.replace(/"/g, '""')}"` : t;
}

function downloadCsv(filename, rows) {
  const blob = new Blob([rows.join("\n")], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click();
  document.body.removeChild(a); URL.revokeObjectURL(url);
}

// ── Sortable column header ────────────────────────────────────────────────────
function SortableHeader({ column, label }) {
  const sort = column.getIsSorted();
  return (
    <button
      onClick={() => column.toggleSorting(sort === "asc")}
      className="flex items-center gap-1.5 text-xs font-semibold text-gray-600 uppercase tracking-wide hover:text-gray-900 transition-colors"
    >
      {label}
      {sort === "asc" ? <ArrowUp className="w-3.5 h-3.5 text-indigo-500" />
        : sort === "desc" ? <ArrowDown className="w-3.5 h-3.5 text-indigo-500" />
          : <ArrowUpDown className="w-3.5 h-3.5 opacity-40" />}
    </button>
  );
}

// ── Row actions dropdown ──────────────────────────────────────────────────────
function RowActions({ row, onEdit, onDuplicate, onDelete, deleting }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="h-7 w-7">
          {deleting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <MoreHorizontal className="w-3.5 h-3.5" />}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-40">
        <DropdownMenuLabel className="text-xs">Actions</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => onEdit(row)} className="gap-2 cursor-pointer">
          <Pencil className="w-3.5 h-3.5" /> Edit
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => onDuplicate(row)} className="gap-2 cursor-pointer">
          <Copy className="w-3.5 h-3.5" /> Duplicate
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={() => onDelete(row)}
          className="gap-2 cursor-pointer text-red-600 focus:text-red-600 focus:bg-red-50"
        >
          <Trash2 className="w-3.5 h-3.5" /> Delete
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// ── Toast ─────────────────────────────────────────────────────────────────────
function Toast({ message, onClose }) {
  if (!message) return null;
  const isError = message.type === "error";
  const isWarn = message.type === "warning";
  return (
    <div className={`flex items-center gap-3 px-4 py-3 rounded-lg border text-sm font-medium shadow-sm mb-4 ${
      isError ? "bg-red-50 border-red-200 text-red-700"
        : isWarn ? "bg-amber-50 border-amber-200 text-amber-700"
          : "bg-green-50 border-green-200 text-green-700"
    }`}>
      {isError ? <AlertCircle className="w-4 h-4 shrink-0" />
        : <CheckCircle2 className="w-4 h-4 shrink-0" />}
      <span className="flex-1">{message.text}</span>
      <button onClick={onClose} className="hover:opacity-60 transition-opacity">
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}

// ── Add / Edit Modal ──────────────────────────────────────────────────────────
function RowModal({ title, form, onChange, onSave, onClose, saving }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl border border-gray-200 shadow-2xl w-full max-w-md">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="text-base font-semibold text-gray-800">{title}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="px-6 py-5 space-y-4">
          {[
            { key: "field_name", label: "Field Name", placeholder: "e.g. business_type" },
            { key: "value", label: "Value", placeholder: "e.g. b2b" },
            { key: "label", label: "Label", placeholder: "e.g. B2B" },
          ].map(({ key, label, placeholder }) => (
            <div key={key} className="space-y-1.5">
              <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">{label}</label>
              <Input
                value={form[key]}
                onChange={(e) => onChange(key, e.target.value)}
                placeholder={placeholder}
                className="h-9"
                onKeyDown={(e) => e.key === "Enter" && onSave()}
              />
            </div>
          ))}
        </div>
        <div className="flex justify-end gap-2 px-6 py-4 border-t border-gray-100 bg-gray-50 rounded-b-2xl">
          <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
          <Button size="sm" onClick={onSave} disabled={saving} className="gap-1.5">
            {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            {saving ? "Saving…" : "Save"}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function DropdownAdmin() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(null);

  // table state
  const [sorting, setSorting] = useState([]);
  const [globalFilter, setGlobalFilter] = useState("");
  const [columnFilters, setColumnFilters] = useState([]);
  const [columnVisibility, setColumnVisibility] = useState({});
  const [rowSelection, setRowSelection] = useState({});
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 25 });

  // modals
  const [addForm, setAddForm] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef(null);

  const showMsg = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 4000);
  };

  // ── API ───────────────────────────────────────────────────────────────────
  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BASE_URL}/dropdowns/?page_size=2000`, { headers: getAuthHeader() });
      const json = await res.json();
      const rows = Array.isArray(json?.results) ? json.results
        : Array.isArray(json) ? json : [];
      setData(rows);
    } catch (e) {
      showMsg("error", "Failed to load dropdown values");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const apiCreate = async (payload) => {
    const res = await fetch(`${BASE_URL}/dropdowns/`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json?.message || JSON.stringify(json?.errors) || "Create failed");
    return json;
  };

  const apiUpdate = async (id, payload) => {
    const res = await fetch(`${BASE_URL}/dropdowns/${id}/`, {
      method: "PUT",
      headers: { "Content-Type": "application/json", ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json?.message || "Update failed");
    return json;
  };

  const apiDelete = async (id) => {
    const res = await fetch(`${BASE_URL}/dropdowns/${id}/`, { method: "DELETE", headers: getAuthHeader() });
    if (!res.ok) throw new Error("Delete failed");
  };

  // ── Row actions ───────────────────────────────────────────────────────────
  const handleAdd = async () => {
    const { field_name, value, label } = addForm;
    if (!field_name.trim() || !value.trim() || !label.trim()) {
      showMsg("warning", "Please fill all fields."); return;
    }
    setSaving(true);
    try {
      const created = await apiCreate({ field_name: field_name.trim(), value: value.trim(), label: label.trim() });
      setData((prev) => [...prev, created]);
      setAddForm(null);
      showMsg("success", "Dropdown value added.");
    } catch (e) { showMsg("error", e.message); }
    finally { setSaving(false); }
  };

  const handleEdit = async () => {
    const { id, field_name, value, label } = editForm;
    if (!field_name.trim() || !value.trim() || !label.trim()) {
      showMsg("warning", "Please fill all fields."); return;
    }
    setSaving(true);
    try {
      const updated = await apiUpdate(id, { field_name: field_name.trim(), value: value.trim(), label: label.trim() });
      setData((prev) => prev.map((r) => r.id === id ? updated : r));
      setEditForm(null);
      showMsg("success", "Updated.");
    } catch (e) { showMsg("error", e.message); }
    finally { setSaving(false); }
  };

  const handleDelete = async (row) => {
    if (!window.confirm(`Delete "${row.label}" (${row.field_name})?`)) return;
    setDeletingId(row.id);
    try {
      await apiDelete(row.id);
      setData((prev) => prev.filter((r) => r.id !== row.id));
      setRowSelection({});
      showMsg("success", "Deleted.");
    } catch (e) { showMsg("error", e.message); }
    finally { setDeletingId(null); }
  };

  const handleDuplicate = async (row) => {
    const newValue = `${row.value}_copy`;
    try {
      const created = await apiCreate({ field_name: row.field_name, value: newValue, label: `${row.label} (Copy)` });
      setData((prev) => [...prev, created]);
      showMsg("success", `Duplicated to "${newValue}".`);
    } catch (e) { showMsg("error", e.message); }
  };

  const handleBulkDelete = async () => {
    const selected = table.getSelectedRowModel().rows.map((r) => r.original);
    if (!selected.length) return;
    if (!window.confirm(`Delete ${selected.length} selected row(s)?`)) return;
    try {
      await Promise.all(selected.map((r) => apiDelete(r.id)));
      const ids = new Set(selected.map((r) => r.id));
      setData((prev) => prev.filter((r) => !ids.has(r.id)));
      setRowSelection({});
      showMsg("success", `Deleted ${selected.length} rows.`);
    } catch (e) { showMsg("error", e.message); }
  };

  // ── CSV export ────────────────────────────────────────────────────────────
  const handleExport = () => {
    const rows = table.getFilteredRowModel().rows.map((r) => r.original);
    const lines = [
      "id,field_name,value,label",
      ...rows.map((r) => [r.id, r.field_name, r.value, r.label].map(csvEscape).join(",")),
    ];
    downloadCsv("dropdown_export.csv", lines);
  };

  const handleExportSample = () => {
    downloadCsv("dropdown_sample.csv", [
      "field_name,value,label",
      "business_type,b2b,B2B",
      "business_type,b2c,B2C",
      "sku_status,active,Active",
    ]);
  };

  // ── CSV import ────────────────────────────────────────────────────────────
  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const matrix = parseCSV(await file.text());
      if (!matrix.length) throw new Error("CSV is empty");
      const headers = matrix[0].map((h) => h.trim().toLowerCase());
      const idx = (names) => names.map((n) => headers.indexOf(n)).find((i) => i >= 0) ?? -1;
      const fi = idx(["field_name", "field"]);
      const vi = idx(["value"]);
      const li = idx(["label", "name"]);
      if (fi < 0 || vi < 0 || li < 0) throw new Error("CSV needs headers: field_name, value, label");

      let created = 0, failed = 0;
      for (const row of matrix.slice(1)) {
        const field_name = (row[fi] || "").trim();
        const value = (row[vi] || "").trim();
        const label = (row[li] || "").trim();
        if (!field_name && !value && !label) continue;
        try {
          const r = await apiCreate({ field_name, value, label });
          setData((prev) => [...prev, r]);
          created++;
        } catch { failed++; }
      }
      showMsg(failed ? "warning" : "success", `Imported ${created} rows${failed ? `, ${failed} failed` : ""}.`);
    } catch (err) { showMsg("error", err.message); }
    finally { setImporting(false); e.target.value = ""; }
  };

  // ── Unique field names for filter ─────────────────────────────────────────
  const fieldOptions = useMemo(
    () => [...new Set(data.map((r) => r.field_name).filter(Boolean))].sort(),
    [data]
  );

  const fieldFilterValue = columnFilters.find((f) => f.id === "field_name")?.value ?? "";

  const setFieldFilter = (val) => {
    setColumnFilters((prev) => {
      const rest = prev.filter((f) => f.id !== "field_name");
      return val ? [...rest, { id: "field_name", value: val }] : rest;
    });
    setPagination((p) => ({ ...p, pageIndex: 0 }));
  };

  // ── TanStack table columns ────────────────────────────────────────────────
  const columns = useMemo(() => [
    {
      id: "select",
      header: ({ table }) => (
        <Checkbox
          checked={table.getIsAllPageRowsSelected() || (table.getIsSomePageRowsSelected() && "indeterminate")}
          onCheckedChange={(v) => table.toggleAllPageRowsSelected(!!v)}
          aria-label="Select all"
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(v) => row.toggleSelected(!!v)}
          aria-label="Select row"
        />
      ),
      enableSorting: false,
      enableGlobalFilter: false,
      size: 40,
    },
    {
      accessorKey: "id",
      header: () => <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">ID</span>,
      cell: ({ getValue }) => <span className="text-gray-400 text-xs font-mono">{getValue()}</span>,
      enableGlobalFilter: false,
      size: 60,
    },
    {
      accessorKey: "field_name",
      header: ({ column }) => <SortableHeader column={column} label="Field Name" />,
      cell: ({ getValue }) => (
        <Badge variant="outline" className="font-mono text-xs text-indigo-700 border-indigo-200 bg-indigo-50">
          {getValue()}
        </Badge>
      ),
    },
    {
      accessorKey: "value",
      header: ({ column }) => <SortableHeader column={column} label="Value" />,
      cell: ({ getValue }) => <span className="font-mono text-sm text-gray-700">{getValue()}</span>,
    },
    {
      accessorKey: "label",
      header: ({ column }) => <SortableHeader column={column} label="Label" />,
      cell: ({ getValue }) => <span className="text-sm text-gray-800">{getValue()}</span>,
    },
    {
      id: "actions",
      header: () => <span className="sr-only">Actions</span>,
      cell: ({ row }) => (
        <RowActions
          row={row.original}
          onEdit={(r) => setEditForm({ ...r })}
          onDuplicate={handleDuplicate}
          onDelete={handleDelete}
          deleting={deletingId === row.original.id}
        />
      ),
      enableSorting: false,
      enableGlobalFilter: false,
      size: 60,
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [deletingId]);

  const table = useReactTable({
    data,
    columns,
    state: { sorting, globalFilter, columnFilters, columnVisibility, rowSelection, pagination },
    onSortingChange: setSorting,
    onGlobalFilterChange: (v) => { setGlobalFilter(v); setPagination((p) => ({ ...p, pageIndex: 0 })); },
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  const selectedCount = Object.keys(rowSelection).length;
  const { pageIndex, pageSize } = pagination;
  const pageCount = table.getPageCount();
  const totalFiltered = table.getFilteredRowModel().rows.length;

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      <div className="max-w-screen-xl mx-auto px-4 py-6 space-y-4">

        {/* ── Page header ──────────────────────────────────────────────────── */}
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Dropdown Admin</h1>
            <p className="text-sm text-gray-500 mt-0.5">Manage dropdown values used across SKU workflows</p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" size="sm" onClick={fetchAll} disabled={loading} className="gap-1.5">
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
            <Button variant="outline" size="sm" onClick={handleExportSample} className="gap-1.5">
              <Download className="w-3.5 h-3.5" /> Sample CSV
            </Button>
            <Button variant="outline" size="sm" onClick={handleExport} className="gap-1.5">
              <Download className="w-3.5 h-3.5" /> Export
            </Button>
            <Button
              variant="outline" size="sm"
              onClick={() => fileInputRef.current?.click()}
              disabled={importing}
              className="gap-1.5"
            >
              {importing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
              Import CSV
            </Button>
            <input ref={fileInputRef} type="file" accept=".csv" className="hidden" onChange={handleImport} />
            <Button size="sm" onClick={() => setAddForm({ field_name: "", value: "", label: "" })} className="gap-1.5">
              <Plus className="w-3.5 h-3.5" /> Add Row
            </Button>
          </div>
        </div>

        <Toast message={message} onClose={() => setMessage(null)} />

        {/* ── Stats ────────────────────────────────────────────────────────── */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: "Total Rows", value: data.length },
            { label: "Field Names", value: fieldOptions.length },
            { label: "Filtered", value: totalFiltered },
            { label: "Selected", value: selectedCount },
          ].map(({ label, value }) => (
            <div key={label} className="bg-white rounded-xl border border-gray-200 px-4 py-3 shadow-sm">
              <p className="text-xs text-gray-500 font-medium">{label}</p>
              <p className="text-2xl font-bold text-gray-900 mt-0.5">{value}</p>
            </div>
          ))}
        </div>

        {/* ── Toolbar ──────────────────────────────────────────────────────── */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
          <div className="flex flex-wrap items-center gap-2 px-4 py-3 border-b border-gray-100">

            {/* Global search */}
            <div className="relative flex-1 min-w-48">
              <Input
                placeholder="Search field, value, label…"
                value={globalFilter}
                onChange={(e) => setGlobalFilter(e.target.value)}
                className="h-8 pl-3 pr-8 text-sm"
              />
              {globalFilter && (
                <button
                  onClick={() => setGlobalFilter("")}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>

            {/* Field name filter */}
            <select
              value={fieldFilterValue}
              onChange={(e) => setFieldFilter(e.target.value)}
              className="h-8 rounded-md border border-gray-200 bg-white text-sm px-2 text-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 min-w-36"
            >
              <option value="">All Fields</option>
              {fieldOptions.map((f) => <option key={f} value={f}>{f}</option>)}
            </select>

            {/* Rows per page */}
            <select
              value={pageSize}
              onChange={(e) => { setPagination({ pageIndex: 0, pageSize: Number(e.target.value) }); }}
              className="h-8 rounded-md border border-gray-200 bg-white text-sm px-2 text-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              {[10, 25, 50, 100].map((s) => <option key={s} value={s}>{s} / page</option>)}
            </select>

            <div className="ml-auto flex items-center gap-2">
              {/* Bulk delete */}
              {selectedCount > 0 && (
                <Button variant="outline" size="sm" onClick={handleBulkDelete}
                  className="gap-1.5 text-red-600 border-red-200 hover:bg-red-50">
                  <Trash2 className="w-3.5 h-3.5" /> Delete {selectedCount}
                </Button>
              )}

              {/* Column visibility */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="gap-1.5">
                    <Settings2 className="w-3.5 h-3.5" /> Columns
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-44">
                  <DropdownMenuLabel className="text-xs">Toggle columns</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {table.getAllColumns()
                    .filter((col) => col.getCanHide())
                    .map((col) => (
                      <DropdownMenuCheckboxItem
                        key={col.id}
                        checked={col.getIsVisible()}
                        onCheckedChange={(v) => col.toggleVisibility(!!v)}
                        className="capitalize text-sm"
                      >
                        {col.id === "field_name" ? "Field Name" : col.id}
                      </DropdownMenuCheckboxItem>
                    ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>

          {/* ── Table ────────────────────────────────────────────────────── */}
          <div className="overflow-auto">
            <Table>
              <TableHeader>
                {table.getHeaderGroups().map((hg) => (
                  <TableRow key={hg.id} className="bg-gray-50/80 hover:bg-gray-50/80">
                    {hg.headers.map((header) => (
                      <TableHead key={header.id} style={{ width: header.getSize() }}>
                        {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                      </TableHead>
                    ))}
                  </TableRow>
                ))}
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={columns.length} className="h-32 text-center">
                      <div className="flex flex-col items-center gap-2 text-gray-400">
                        <Loader2 className="w-6 h-6 animate-spin" />
                        <span className="text-sm">Loading…</span>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : table.getRowModel().rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={columns.length} className="h-32 text-center text-gray-400 text-sm">
                      No rows found.
                    </TableCell>
                  </TableRow>
                ) : (
                  table.getRowModel().rows.map((row) => (
                    <TableRow key={row.id} data-state={row.getIsSelected() ? "selected" : undefined}
                      className="hover:bg-indigo-50/30 transition-colors">
                      {row.getVisibleCells().map((cell) => (
                        <TableCell key={cell.id}>
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {/* ── Pagination ───────────────────────────────────────────────── */}
          <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-t border-gray-100 bg-gray-50/50">
            <span className="text-xs text-gray-500">
              {selectedCount > 0 ? `${selectedCount} of ` : ""}{totalFiltered} row{totalFiltered !== 1 ? "s" : ""}
              {globalFilter || fieldFilterValue ? " (filtered)" : ""}
            </span>

            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => table.setPageIndex(0)} disabled={!table.getCanPreviousPage()}>«</Button>
              <Button variant="outline" size="sm" onClick={() => table.previousPage()} disabled={!table.getCanPreviousPage()}>‹</Button>
              <span className="text-xs text-gray-600 font-medium px-1">
                Page {pageIndex + 1} of {pageCount || 1}
              </span>
              <Button variant="outline" size="sm" onClick={() => table.nextPage()} disabled={!table.getCanNextPage()}>›</Button>
              <Button variant="outline" size="sm" onClick={() => table.setPageIndex(pageCount - 1)} disabled={!table.getCanNextPage()}>»</Button>
            </div>
          </div>
        </div>
      </div>

      {/* ── Add modal ──────────────────────────────────────────────────────── */}
      {addForm && (
        <RowModal
          title="Add Dropdown Value"
          form={addForm}
          onChange={(k, v) => setAddForm((p) => ({ ...p, [k]: v }))}
          onSave={handleAdd}
          onClose={() => setAddForm(null)}
          saving={saving}
        />
      )}

      {/* ── Edit modal ─────────────────────────────────────────────────────── */}
      {editForm && (
        <RowModal
          title="Edit Dropdown Value"
          form={editForm}
          onChange={(k, v) => setEditForm((p) => ({ ...p, [k]: v }))}
          onSave={handleEdit}
          onClose={() => setEditForm(null)}
          saving={saving}
        />
      )}
    </div>
  );
}
